/**
 * Small pure parse/format helpers for map URL state.
 * Parsers return `undefined` when the input is malformed.
 */

/**
 * Round a number to a fixed number of decimals, normalizing -0 to 0.
 */
export const roundTo = (value, decimals) => {
  const factor = Math.pow(10, decimals);
  const rounded = Math.round(value * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
};

/**
 * Strict decimal number parse. Rejects empty strings, whitespace, hex,
 * exponents and non-finite values.
 */
export const parseNumber = (str) => {
  if (typeof str !== "string" || !/^-?(\d+\.?\d*|\.\d+)$/.test(str)) {
    return undefined;
  }
  const value = Number(str);
  return Number.isFinite(value) ? value : undefined;
};

/**
 * Strict non-negative integer parse (digits only).
 */
export const parseNonNegativeInt = (str) => {
  if (typeof str !== "string" || !/^\d+$/.test(str)) return undefined;
  const value = Number(str);
  return Number.isSafeInteger(value) ? value : undefined;
};

/**
 * `1` -> true, `0` -> false, anything else -> undefined.
 */
export const parseBool01 = (str) => {
  if (str === "1") return true;
  if (str === "0") return false;
  return undefined;
};

export const formatBool01 = (value) => (value ? "1" : "0");

/**
 * Parse a comma-joined list of non-negative integers.
 * An empty string is an empty list. Returns `{ values, invalid }` where
 * `invalid` holds the tokens that could not be parsed.
 */
export const parseIntList = (str) => {
  const values = [];
  const invalid = [];
  if (!str) return { values, invalid };
  str.split(",").forEach((token) => {
    const value = parseNonNegativeInt(token.trim());
    if (value === undefined) {
      if (token !== "") invalid.push(token);
    } else if (!values.includes(value)) {
      values.push(value);
    }
  });
  return { values, invalid };
};

export const formatIntList = (values) => values.join(",");

/**
 * Parse a comma-joined list of string keys, ignoring empty entries and
 * duplicates.
 */
export const parseKeyList = (str) => {
  if (!str) return [];
  return str
    .split(",")
    .filter(
      (token, index, arr) => token !== "" && arr.indexOf(token) === index
    );
};

export const formatKeyList = (values) => values.join(",");

/**
 * Parse `layerId:NN` where NN is an integer 0..100.
 * Returns `{ id, value }` (value in 0..1) or undefined.
 */
export const parseOpacityPair = (str) => {
  if (typeof str !== "string") return undefined;
  const index = str.lastIndexOf(":");
  if (index <= 0) return undefined;
  const id = str.slice(0, index);
  const percent = parseNonNegativeInt(str.slice(index + 1));
  if (percent === undefined || percent > 100) return undefined;
  return { id, value: percent / 100 };
};

export const formatOpacityPair = (id, value) => {
  const percent = Math.min(100, Math.max(0, Math.round(value * 100)));
  return `${id}:${percent}`;
};

/**
 * Wrap a longitude into [-180, 180].
 */
export const wrapLng = (lng) => {
  if (lng >= -180 && lng <= 180) return lng;
  return ((((lng + 180) % 360) + 360) % 360) - 180;
};

/**
 * Round a camera to the precision used in the URL.
 */
export const roundCamera = (camera) => ({
  zoom: roundTo(camera.zoom, 2),
  lat: roundTo(camera.lat, 5),
  lng: roundTo(wrapLng(camera.lng), 5),
  bearing: roundTo(camera.bearing || 0, 1),
  pitch: roundTo(camera.pitch || 0, 1),
});

/**
 * Parse `zoom/lat/lng[/bearing[/pitch]]`.
 * Validates zoom 0..24, lat -90..90, lng -180..180.
 */
export const parseCamera = (str) => {
  if (typeof str !== "string") return undefined;
  const parts = str.split("/");
  if (parts.length < 3 || parts.length > 5) return undefined;
  const numbers = parts.map(parseNumber);
  if (numbers.some((n) => n === undefined)) return undefined;
  const [zoom, lat, lng, bearing = 0, pitch = 0] = numbers;
  if (zoom < 0 || zoom > 24) return undefined;
  if (lat < -90 || lat > 90) return undefined;
  if (lng < -180 || lng > 180) return undefined;
  return { zoom, lat, lng, bearing, pitch };
};

export const formatCamera = (camera) => {
  const { zoom, lat, lng, bearing, pitch } = roundCamera(camera);
  const parts = [zoom, lat, lng];
  if (bearing !== 0 || pitch !== 0) parts.push(bearing, pitch);
  return parts.map(String).join("/");
};

/**
 * encodeURIComponent, but keep `,`, `/` and `:` readable. These are all
 * safe inside a query string value and decode unchanged via URLSearchParams.
 */
export const encodeQueryComponent = (str) =>
  encodeURIComponent(str)
    .replace(/%2C/gi, ",")
    .replace(/%2F/gi, "/")
    .replace(/%3A/gi, ":");

export const formatPair = (key, value) =>
  `${encodeQueryComponent(key)}=${encodeQueryComponent(value)}`;

/**
 * Decode a raw query string key the same way URLSearchParams does, without
 * throwing on malformed percent sequences.
 */
export const decodeQueryComponent = (str) => {
  const plusDecoded = str.replace(/\+/g, " ");
  try {
    return decodeURIComponent(plusDecoded);
  } catch (e) {
    return plusDecoded;
  }
};

/**
 * Split a search string into raw segments, keeping the original text of
 * each `key=value` pair along with its decoded key.
 */
export const splitSearch = (search) => {
  const str = (search || "").replace(/^\?/, "");
  if (!str) return [];
  return str
    .split("&")
    .filter((segment) => segment !== "")
    .map((segment) => {
      const eq = segment.indexOf("=");
      const rawKey = eq === -1 ? segment : segment.slice(0, eq);
      return { key: decodeQueryComponent(rawKey), raw: segment };
    });
};
